import { type Flash as VisitFlash, useVisit } from "@/lib/page-rendering"
import { Alert, AlertDescription, AlertTitle } from "./alert"
import { useTranslation } from "react-i18next"
import { Namespace, ParseKeys } from "i18next"
import { AlertCircle, CheckCircle, LucideIcon } from "lucide-react"

export type FlashType = "success" | "alert"

export type ResolvedFlash = { type: FlashType; message: string }

type FlashEntry = {
  type: FlashType
  message: NonNullable<VisitFlash["success"]>
}

function flashEntry(flash: VisitFlash) {
  if ("success" in flash) {
    return { type: "success", message: flash.success! } satisfies FlashEntry
  }
  if ("alert" in flash) {
    return { type: "alert", message: flash.alert! } satisfies FlashEntry
  }
  return null
}

// The visit's flash with its message resolved to text. The payload is the
// shared prop object itself, so a caller can tell one delivery from the next
// even when two carry the same words.
//
// The message's namespace is the one its controller names, which differs
// from one visit to the next while this hook's component stays mounted (a
// POST keeps the page's state). useTranslation loads the namespace, but the
// t it hands back stays bound to the namespace it first rendered with, so
// the message is translated with a t fixed to its own namespace each time.
export function useResolvedFlash(): {
  payload: object
  resolved: ResolvedFlash | null
} {
  const { flash } = useVisit()
  const entry = flashEntry(flash)
  const namespace =
    entry !== null && typeof entry.message !== "string"
      ? (entry.message.namespace as Namespace)
      : undefined
  const { i18n } = useTranslation(namespace)

  if (entry === null) return { payload: flash, resolved: null }

  const message =
    typeof entry.message === "string"
      ? entry.message
      : i18n.getFixedT(null, namespace ?? null)(entry.message.key as ParseKeys)

  return { payload: flash, resolved: { type: entry.type, message } }
}

export default function Flash() {
  const { resolved } = useResolvedFlash()

  if (resolved === null) return null

  return <FlashAlert {...resolved} />
}

const FLASH_ICONS: Record<FlashType, LucideIcon> = {
  success: CheckCircle,
  alert: AlertCircle,
}

export function FlashAlert({
  type,
  message,
  ...props
}: ResolvedFlash & React.ComponentProps<typeof Alert>) {
  const { t } = useTranslation("common")
  const Icon = FLASH_ICONS[type]

  return (
    <Alert
      variant={type === "alert" ? "destructive" : "default"}
      {...props}
      data-testid="flash"
    >
      <Icon className="h-4 w-4" />
      <AlertTitle>{t(`flash.${type}`)}</AlertTitle>
      <AlertDescription data-testid={`${type}-message`}>
        {message}
      </AlertDescription>
    </Alert>
  )
}
